import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import HTMLFlipBook from 'react-pageflip';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import './ClientManual.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFFlipViewerProps {
    pdfUrl: string;
    forcedHeight?: number | { large?: number; medium?: number; small?: number };
}


const Flipbook: React.FC<PDFFlipViewerProps> = ({ pdfUrl, forcedHeight }) => {
    const [numPages, setNumPages] = useState<number>(0);
    const [screenSize, setScreenSize] = useState<'large' | 'medium' | 'small'>('large');

    useEffect(() => {
        const updateScreenSize = () => {
            const width = window.innerWidth;
            if (width <= 530) {
                setScreenSize('small');
            } else if (width <= 1140) {
                setScreenSize('medium');
            } else {
                setScreenSize('large');
            }
        };

        updateScreenSize(); // run once at mount
        window.addEventListener('resize', updateScreenSize);
        return () => window.removeEventListener('resize', updateScreenSize);
    }, []);

    function toRoman(num: number): string {
        const romanMap: [number, string][] = [
            [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
            [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
            [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'],
            [1, 'I']
        ];
        let result = '';
        for (const [value, numeral] of romanMap) {
            while (num >= value) {
                result += numeral;
                num -= value;
            }
        }
        return result;
    }

    // Dimensions based on screen size
    const dimensions = {
        large: { width: 570, height: 750, pageWidth: 550 },
        medium: { width: 470, height: 630, pageWidth: 455 },
        small: { width: 370, height: 500, pageWidth: 355 }
    };

    const { width, height: defaultHeight, pageWidth } = dimensions[screenSize];
    let height: number;

    if (typeof forcedHeight === 'number') {
        height = forcedHeight;
    } else if (typeof forcedHeight === 'object' && forcedHeight !== null) {
        height = forcedHeight[screenSize] ?? defaultHeight;
    } else {
        height = defaultHeight;
    }



    return (
        <div>
            <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            >
                <div className='w-screen flex flex-col overflow-hidden'>
                    <HTMLFlipBook
                        width={width}
                        height={height}
                        size="fixed"
                        minWidth={315}
                        maxWidth={1000}
                        minHeight={400}
                        maxHeight={1536}
                        maxShadowOpacity={1}
                        showCover={true}
                        mobileScrollSupport={true}
                        style={{ margin: '0 auto' }}
                        className="flipbook"
                        startPage={1}
                        drawShadow={true}
                        flippingTime={1000}
                        usePortrait={true}
                        startZIndex={0}
                        autoSize={true}
                        clickEventForward={true}
                        useMouseEvents={true}
                        swipeDistance={30}
                        showPageCorners={true}
                        disableFlipByClick={false}
                    >
                        {Array.from(new Array(numPages), (_, index) => (
                            <div key={`page_${index + 1}`} className="pdf-page-wrapper">
                                <div className="page-border">
                                    <div className="page-content">
                                        <Page
                                            pageNumber={index + 1}
                                            width={pageWidth}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                        />
                                        {(index !== 0 && index !== numPages - 1) && (
                                            <div className="page-number">
                                                {toRoman(index + 1)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </HTMLFlipBook>
                </div>
            </Document>
        </div>
    );
};

export default Flipbook;
